import {
  ContentTypeOptions,
  SourceFilterOptions,
} from '../enums/FilterOptions';
import { CategoryAppliesTo } from '../enums/category';
import { z } from 'zod';

// The largest page any `QueryAllDto` list accepts. Exported so a "load more"
// that grows `limit` (rather than paging) can stop at the ceiling instead of
// tripping the 400 below.
export const QUERY_ALL_LIMIT_MAX = 100;

// Reserved `organizationIds` entry meaning "explicitly zero organizations" —
// a querystring cannot carry a present-but-empty array (`qs.stringify({x:
// []})` and omitting `x` both produce nothing), so callers that want NO
// organization-owned rows (categories' "Platform Categories only" view) send
// this sentinel instead of an empty array. A caller that omits
// `organizationIds` altogether gets each endpoint's own default scope.
export const NO_ORGANIZATIONS = 'none';

// SHAPE ONLY, deliberately: every id in this codebase is a `DataTypes.UUIDV4`
// PK, but the point of the check is that a segment cannot carry a quote, a
// comma or a comment terminator — pinning the version/variant nibbles would
// only add ways to reject an id Postgres accepts. Case-insensitive: Postgres
// prints lowercase but reads either.
const UUID_SHAPE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * True when `value` is a comma-separated list of UUIDs, ignoring the whitespace
 * around each one. An empty segment (a trailing comma, `a,,b`) is not an id, so
 * it fails.
 *
 * SQL INJECTION (S1): the controllers split these values and hand the pieces to
 * `IN (...)` fragments — `equipmentTagIds` was pasted in one `'${id}'` at a
 * time, so `x') OR 1=1 --` closed the quote and the filter matched every row.
 * Shared with `equipmentSQL.ts`, which re-checks at the SQL chokepoint.
 */
export const isCommaSeparatedUuids = (value: string) =>
  value.split(',').every((segment) => UUID_SHAPE.test(segment.trim()));

/** A comma-separated list of ids, as the query string carries it. */
const uuidList = (max: number) =>
  z
    .string()
    .min(1)
    .max(max)
    // A KEY, not a sentence. `zod.filter.ts` looks a zod message up by name in
    // `@mfw/translations/validationMessages`; an English sentence misses that
    // lookup and `TranslationService` answers "Unknown, message could not be
    // translated" — in every locale — instead of the refusal.
    .refine(isCommaSeparatedUuids, {
      message: 'VALIDATION__InvalidIdList',
    })
    .optional();

export const QueryAllDto = z
  .object({
    include: z.array(z.string()).optional(),
    orderBy: z.string().min(1).max(255).optional(),
    orderDirection: z.string().min(1).max(4).optional(),
    search: z.string().trim().max(255).optional(),
    ids: uuidList(255),
    tagIds: uuidList(255),
    equipmentTagIds: uuidList(255),
    workoutIds: uuidList(255),
    exerciseIds: uuidList(255),
    programIds: uuidList(255),
    offset: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(QUERY_ALL_LIMIT_MAX).default(25),
    filter: z.record(z.string(), z.string()).optional(),
    types: z.string().optional(),
    includePlatform: z
      .enum(['true', 'false'])
      .transform((value) => value === 'true')
      .optional(),
    // Deprecated compatibility input. Marketplace visibility no longer has a
    // separate public branch, but older clients must not fail strict parsing
    // while they migrate to the current query contract.
    withPublic: z
      .enum(['true', 'false'])
      .transform((value) => value === 'true')
      .optional(),
    isPublic: z
      .enum(['true', 'false'])
      .transform((value) => value === 'true')
      .optional(),
    organizationIds: z
      .preprocess((val) => {
        if (!val) return undefined;
        return Array.isArray(val) ? val : [val];
      }, z.array(z.string()))
      .optional(),
    trainerIds: z.array(z.string()).optional(),
    authorIds: z.array(z.string()).optional(),
    selectedUserIds: z.array(z.string()).optional(),
    sources: z.array(z.nativeEnum(SourceFilterOptions)).optional(),
    contentType: z.array(z.nativeEnum(ContentTypeOptions)).optional(),
    isMandatory: z
      .enum(['true', 'false'])
      .transform((value) => value === 'true')
      .optional(),
    status: z
      .string()
      .toUpperCase()
      .pipe(z.enum(['ACTIVE', 'ARCHIVED', 'PENDING', 'ALL', 'REJECTED']))
      .optional()
      .or(z.literal('')),
    withoutTrainer: z
      .enum(['true', 'false'])
      .transform((value) => value === 'true')
      .optional(),
    membershipIds: uuidList(4096),
    withinLibrary: z.boolean().optional(),
    isDeleted: z.boolean().optional(),
    isMarketplace: z.boolean().optional(),
    isHome: z.boolean().optional(),
    // Retired picker flag (MFW content for trainers, release 1): accepted and
    // ignored for one release, so a frontend that still sends it is not
    // refused by this strict DTO; nothing reads it. Remove it in the next
    // release.
    selectable: z.boolean().optional(),
    appliesTo: z
      .preprocess(
        (val) => {
          if (!val) return [];
          return Array.isArray(val) ? val : [val];
        },
        z.array(z.nativeEnum(CategoryAppliesTo)),
      )
      .optional(),
  })
  .strict();

export type ApiQueryAllDto = z.infer<typeof QueryAllDto>;
